import React from 'react';
import PropTypes from 'prop-types';
import ListItemActions from './ListItemActions';

// Modal that lists all sessions for a single calendar day.
// Edit/Delete buttons hand off to the parent's existing handlers, which open
// the existing EditDialog / DeleteDialog. The modal does not own those flows.
const SessionDetailsModal = ({
  isOpen,
  onClose,
  dateLabel,
  sessions,
  onEditSession,
  onDeleteSession,
  formatDuration,
  darkMode = false,
}) => {
  if (!isOpen) return null;

  const cls = darkMode ? {
    overlay: 'tw-fixed tw-inset-0 tw-bg-black/90 tw-backdrop-blur-lg tw-flex tw-items-center tw-justify-center tw-z-50',
    panel: 'tw-bg-gray-900 tw-rounded-lg tw-p-6 tw-max-w-md tw-w-full tw-mx-4 tw-max-h-[80vh] tw-overflow-y-auto tw-border tw-border-white/10',
    title: 'tw-text-xl tw-font-bold tw-text-white',
    closeBtn: 'tw-appearance-none tw-bg-transparent tw-border-0 tw-text-white/70 hover:tw-text-white tw-cursor-pointer tw-text-2xl tw-leading-none tw-p-0',
    empty: 'tw-text-white/60',
    row: 'tw-bg-white/5 tw-rounded-lg tw-p-3 tw-group',
    rowLabel: 'tw-text-white/60',
    rowValue: 'tw-text-white tw-font-medium',
    notesBorder: 'tw-border-white/10',
    notesValue: 'tw-text-white tw-font-medium tw-whitespace-pre-wrap',
  } : {
    overlay: 'tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50',
    panel: 'tw-bg-white tw-rounded-lg tw-p-6 tw-max-w-md tw-w-full tw-mx-4 tw-max-h-[80vh] tw-overflow-y-auto',
    title: 'tw-text-xl tw-font-bold tw-text-gray-900',
    closeBtn: 'tw-appearance-none tw-bg-transparent tw-border-0 tw-text-gray-500 tw-cursor-pointer tw-text-2xl tw-leading-none tw-p-0',
    empty: 'tw-text-gray-500',
    row: 'tw-bg-gray-50 tw-rounded-lg tw-p-3 tw-group',
    rowLabel: 'tw-text-gray-500',
    rowValue: 'tw-font-medium',
    notesBorder: 'tw-border-gray-200',
    notesValue: 'tw-font-medium tw-whitespace-pre-wrap',
  };

  const editIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="tw-h-4 tw-w-4 tw-text-gray-500 hover:tw-text-gray-700" viewBox="0 0 20 20" fill="currentColor">
      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
    </svg>
  );

  const deleteIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="tw-h-4 tw-w-4 tw-text-gray-500 hover:tw-text-red-500" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
    </svg>
  );

  return (
    <div className={cls.overlay} onClick={onClose}>
      <div className={cls.panel} onClick={(e) => e.stopPropagation()}>
        <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
          <h2 className={cls.title}>{dateLabel}</h2>
          <button onClick={onClose} aria-label="Close" className={cls.closeBtn}>
            ✕
          </button>
        </div>

        {sessions.length === 0 ? (
          <p className={cls.empty}>No sessions on this day.</p>
        ) : (
          <ul className="tw-list-none tw-p-0 tw-m-0 tw-space-y-3">
            {sessions.map((session) => (
              <li key={session._id} className={cls.row}>
                <div className="tw-flex tw-justify-between tw-items-start">
                  <div className="tw-flex tw-flex-col tw-space-y-1 tw-text-sm">
                    <div>
                      <span className={cls.rowLabel}>Time: </span>
                      <span className={cls.rowValue}>
                        {new Date(session.startedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true }).toLowerCase()}
                      </span>
                    </div>
                    <div>
                      <span className={cls.rowLabel}>Duration: </span>
                      <span className={cls.rowValue}>
                        {formatDuration ? formatDuration(session.duration) : session.duration}
                      </span>
                    </div>
                    {session.project && (
                      <div>
                        <span className={cls.rowLabel}>Project: </span>
                        <span className={cls.rowValue}>{session.project.name}</span>
                      </div>
                    )}
                  </div>
                  <ListItemActions
                    onEdit={onEditSession ? () => onEditSession(session) : undefined}
                    onDelete={onDeleteSession ? () => onDeleteSession(session) : undefined}
                    editIcon={editIcon}
                    deleteIcon={deleteIcon}
                  />
                </div>
                {session.text && (
                  <div className={`tw-text-sm tw-mt-2 tw-pt-2 tw-border-t ${cls.notesBorder}`}>
                    <span className={cls.rowLabel}>Notes: </span>
                    <span className={cls.notesValue}>{session.text}</span>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="tw-flex tw-justify-end tw-mt-6">
          <button onClick={onClose} className={darkMode ? 'secondary-button tw-text-white' : 'secondary-button'}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

SessionDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  dateLabel: PropTypes.string.isRequired,
  sessions: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      startedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
      duration: PropTypes.string,
      text: PropTypes.string,
      project: PropTypes.shape({ _id: PropTypes.string, name: PropTypes.string }),
    })
  ).isRequired,
  onEditSession: PropTypes.func,
  onDeleteSession: PropTypes.func,
  formatDuration: PropTypes.func,
  darkMode: PropTypes.bool,
};

export default SessionDetailsModal;
